<template>
    <div :class="[$style.container, props.hideFilter && $style.noFilter]">
        <template v-if="props.isOwn && !ownTemplates.length">
            <div :class="$style.noTemplate">
                <div
                    :class="[
                        $style.img,
                        { [$style.img_jp]: language === 'jp', [$style.img_en]: language === 'en' }
                    ]"
                ></div>
                <div :class="$style.title">{{ $t('experience.onboarding.no_design_title') }}</div>
                <div :class="$style.desc" v-html="$t('experience.onboarding.no_design_desc')"></div>
            </div>
        </template>
        <template v-else>
            <div v-if="props.hasSearch" :class="$style.search">
                <pt-input
                    v-model="searchVal"
                    :class="$style.input"
                    size="small"
                    clearable
                    :placeholder="$t('experience.common.search')"
                >
                    <template #prefix>
                        <pt-icon
                            icon="pt-icon-search"
                            :style="{
                                width: '24px',
                                height: '24px',
                                'margin-left': '4px',
                                'margin-top': '4px'
                            }"
                        ></pt-icon>
                    </template>
                </pt-input>
            </div>
            <div v-if="!hideFilter" id="pt-onbo-templateFilter" :class="$style.sidebar">
                <TemplateFilter
                    v-if="!props.isOwn"
                    :typeName="$t('experience.onboarding.purpose_title')"
                    :list="purposeFilters"
                ></TemplateFilter>
                <TemplateFilter
                    v-if="!props.isOwn"
                    :typeName="$t('experience.onboarding.industry_title')"
                    :list="industryFilters"
                ></TemplateFilter>
                <TemplateFilter
                    v-if="isPopupType || props.showTypeFilter"
                    :typeName="$t('experience.onboarding.types_title')"
                    :list="typeFilterList"
                ></TemplateFilter>
                <div v-if="props.showConnectUs" :class="$style.connectUs">
                    <p>
                        {{ t('product.no_finding') }}&nbsp;
                        <span :class="$style.trigger" @click="openSuggestDialog">
                            {{ t('product.connect_us') }}</span
                        >
                    </p>
                </div>
            </div>
            <div
                v-loading="props.loading ? ecTemplateLoading || officialTemplatesLoading : false"
                :class="$style.right"
            >
                <TemplateRecommandList
                    size="large"
                    :position="props.position"
                    :entryPosition="props.entryPosition"
                    :needLoadTemplate="false"
                    :engageType="props.engageType"
                    :showBlank="props.showBlank"
                    :showBackground="false"
                    :list="currentFilterList"
                    :emptyTip="props.emptyTip"
                    :loading="!props.loading"
                    :isMobile="isMobile"
                    @view="handleViewTemplate"
                    @create="handleCreateTemplate"
                    @edit="handleEditTemplate"
                    @delete="handleDeleteTemplate"
                ></TemplateRecommandList>
            </div>
            <!-- 编辑和删除弹窗层级需要比start guide层级高 由于scss变量($page-help-index: 5000;)不通用所以把值写死 -->
            <ExperienceTemplateEditDialog
                v-if="state.editDialogVisible"
                :z-index="5002"
                :visible="state.editDialogVisible"
                :template="state.currentEditTemplate"
                @close="state.editDialogVisible = false"
                @save="handleUpdateOwnTemplate"
            ></ExperienceTemplateEditDialog>

            <ExperienceTemplateDeleteDialog
                v-if="state.deleteDialogVisible"
                :z-index="5002"
                :visible="state.deleteDialogVisible"
                :template="state.currentEditTemplate"
                @confirm="confirmDeleteTemplate"
                @close="state.deleteDialogVisible = false"
            ></ExperienceTemplateDeleteDialog>
        </template>
    </div>
</template>

<script lang="ts">
import TemplateFilter from '@experience/components/template/TemplateFilter.vue';
import TemplateRecommandList from '@experience/components/template/TemplateRecommandList.vue';
import ExperienceTemplateEditDialog from '@experience/components/dialog/ExperienceTemplateEditDialog.vue';
import ExperienceTemplateDeleteDialog from '@experience/components/dialog/ExperienceTemplateDeleteDialog.vue';
import { suggest } from '@/utils/common.util';
export default {
    name: 'TemplateListWithFilter',
    components: {
        TemplateFilter,
        TemplateRecommandList,
        ExperienceTemplateEditDialog,
        ExperienceTemplateDeleteDialog
    }
};
</script>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    templateTypeFilterList,
    popupTypeFilterList,
    templatePurposeFilterList,
    templateIndustryFilterList
} from '@experience/static/config/experience.config';
import type { TypeItem } from '@experience/static/config/experience.type';
import type { ExperienceType } from '@models/experience/experience';
import { useAccountInfo } from '@models/account/useAccountInfo';
import {
    useAllTemplates,
    useAllOfficialTemplates
} from '@models/experience/useExperienceTemplatesV4';
import { useECTemplates } from '@models/ecshop/asset';
import { cloneDeep } from 'lodash-es';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import type { ECTemplate } from '@models/ecshop/asset';
import { useRouter, useRoute } from 'vue-router';
import PtMessage from '@ui/message';
import { ECDataLocation } from '@experience/hooks/template.hooks';
import useEventBus from '@assets/plugins/eventBus';
// interface
interface Props {
    position: ECDataLocation;
    entryPosition?: string;
    location: string;
    engageType?: TypeItem;
    hasSearch?: boolean;
    searchContent?: string;
    list?: Array<ExperienceTemplateV4 | ECTemplate>;
    hasOwnTemplates?: boolean;
    activeTab?: 'OWN' | 'COMMON';
    isOwn?: boolean;
    // 是否显示空白创建
    showBlank?: boolean;
    showTypeFilter?: boolean;
    initTemplateId?: string;
    filter?: any;
    saveFilter?: boolean;
    emptyTip?: boolean;
    loading?: boolean;
    hideFilter?: boolean;
    isMobile?: boolean;
    showConnectUs?: boolean;
    webType?: string;
    webTarget?: string;
}
// props
const props = withDefaults(defineProps<Props>(), {
    hasSearch: true,
    searchContent: '',
    hasOwnTemplates: false,
    isOwn: false,
    showBlank: false,
    showTypeFilter: false,
    initTemplateId: '',
    filter: {},
    saveFilter: false,
    emptyTip: false,
    activeTab: 'COMMON',
    loading: true,
    hideFilter: false,
    isMobile: false,
    showConnectUs: false
});
// emits
const emit = defineEmits(['view', 'create', 'edit', 'delete']);
const { eventBus } = useEventBus();
// state
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const searchVal = ref('');
const { account } = useAccountInfo();
const { getECTemplates, allECTemplates, ecTemplateLoading } = useECTemplates();
const purposeFilters = ref(cloneDeep(templatePurposeFilterList));
const industryFilters = ref(cloneDeep(templateIndustryFilterList));
const typeFilterList = ref(
    cloneDeep(props.engageType?.key === 'POPUP' ? popupTypeFilterList : templateTypeFilterList)
);
const typeFilterSelected = ref([]);
const industryFilterSelected = ref([]);
const purposeFilterSelected = ref([]);
const state: {
    editDialogVisible: boolean;
    deleteDialogVisible: boolean;
    currentEditTemplate: ExperienceTemplateV4 | null;
} = reactive({
    editDialogVisible: false,
    deleteDialogVisible: false,
    currentEditTemplate: null
});
// computed
// 根据url判断区域。本地判定为en
const currentArea = computed(() => {
    return /(ptmind|ptengine)\.com/.test(location.host) ? 'en' : 'jp';
});
const language = computed(() => {
    return account.value ? account.value?.language?.toLocaleLowerCase() : currentArea.value;
});
const isPopupType = computed(() => {
    return props.engageType?.key === 'POPUP';
});
const defaultTemplateId = ref(props.initTemplateId);

const {
    officialTemplates,
    ownTemplates,
    officialTemplatesLoading,
    updateOwnTemplate,
    deleteOwnTemplate
} = props.hasOwnTemplates ? useAllOfficialTemplates() : useAllTemplates();

const currentFilterList: any = computed(() => {
    if (!ownTemplates.value || !officialTemplates.value) {
        return [];
    }
    // 根据传参判断搜索值是组件内获取还是组件外传进来
    const searchContent = props.hasSearch ? searchVal.value?.trim() : props.searchContent;
    let officialTemplatesClone = cloneDeep(officialTemplates.value || []);
    if (!props.location || props.location !== 'Create Popup Dialog') {
        officialTemplatesClone = officialTemplatesClone.filter(item => {
            return item.recommendationLocation !== 'Create Popup Dialog';
        });
    }
    let result: Array<ExperienceTemplateV4 | ECTemplate> = props.isOwn
        ? cloneDeep(ownTemplates.value || [])
        : !props.engageType
        ? officialTemplatesClone.concat(allECTemplates.value)
        : props.engageType?.key === 'POPUP'
        ? officialTemplatesClone
        : getECTemplatesByType(props.engageType?.key);
    if (searchContent) {
        const searchContentLowercase = searchContent?.toLocaleLowerCase();
        result = result.filter((item: ExperienceTemplateV4 | ECTemplate) => {
            return (
                (typeof item.i18nName === 'string' &&
                    item.i18nName?.toLocaleLowerCase().indexOf(searchContentLowercase) !== -1) ||
                (typeof item.i18nDesc === 'string' &&
                    item.i18nDesc?.toLocaleLowerCase().indexOf(searchContentLowercase) !== -1) ||
                (typeof item.tags?.type === 'string' &&
                    t(`experience.onboarding.tags.${item.tags?.type}`)
                        .toLocaleLowerCase()
                        ?.indexOf(searchContentLowercase) !== -1) ||
                (item.tags?.abtestType &&
                    t(`experience.onboarding.tags.${item.tags?.abtestType}`)
                        .toLocaleLowerCase()
                        ?.indexOf(searchContentLowercase) !== -1) ||
                searchFromList(item.tags?.industry || [], searchContentLowercase) ||
                searchFromList(item.tags?.purpose || [], searchContentLowercase)
            );
        });
    }
    if (
        typeFilterSelected.value?.length &&
        typeFilterSelected.value?.length !== typeFilterList.value.length
    ) {
        if (isPopupType.value) {
            result = result.filter((item: ExperienceTemplateV4 | ECTemplate) => {
                return typeFilterSelected.value?.includes(item.type?.toLocaleLowerCase() as string);
            });
        } else {
            result = result.filter((item: ExperienceTemplateV4 | ECTemplate) => {
                return (
                    typeFilterSelected.value?.includes(item?.tags?.type as string) ||
                    (typeFilterSelected.value?.includes('ABtesting') && item?.tags.test)
                );
            });
        }
    }

    if (
        industryFilterSelected.value?.length &&
        industryFilterSelected.value?.length !== industryFilters.value.length
    ) {
        result = result.filter((item: ExperienceTemplateV4 | ECTemplate) => {
            return item?.tags?.industry?.some((i: any) => industryFilterSelected.value.includes(i));
        });
    }

    if (
        purposeFilterSelected.value?.length &&
        purposeFilterSelected.value?.length !== purposeFilters.value.length
    ) {
        result = result.filter((item: ExperienceTemplateV4 | ECTemplate) => {
            return item?.tags?.purpose?.some((i: any) => purposeFilterSelected.value.includes(i));
        });
    }
    return result.sort((a, b) => {
        return Number(b.tags?.priority) - Number(a.tags?.priority);
    });
});

// 监听组件外部传来的模版ID，打开模版详情弹框
watch(
    () => (officialTemplates.value, allECTemplates.value),
    () => {
        initShowTemplate();
    },
    {
        immediate: true,
        deep: true
    }
);
watch(
    () => typeFilterList.value,
    () => {
        filter('type');
    },
    {
        deep: true,
        immediate: true
    }
);
watch(
    () => industryFilters.value,
    () => {
        filter('industry');
    },
    {
        deep: true,
        immediate: true
    }
);
watch(
    () => purposeFilters.value,
    () => {
        filter('purpose');
    },
    {
        deep: true,
        immediate: true
    }
);

watch(
    () => props.activeTab,
    (newVal, oldVal) => {
        if (oldVal && newVal !== oldVal) {
            purposeFilters.value = cloneDeep(templatePurposeFilterList);
            industryFilters.value = cloneDeep(templateIndustryFilterList);
            typeFilterList.value = cloneDeep(
                props.engageType?.key === 'POPUP' ? popupTypeFilterList : templateTypeFilterList
            );
        }
    }
);

// onMounted
onMounted(() => {
    if (!allECTemplates.value) {
        getECTemplates();
    }

    props.saveFilter && initFilter();

    if ((props.webType || props.webTarget) && props.entryPosition === 'start_guide') {
        // start guide 来的用户需要默认选中客户档案的行业和目的相关分类
        if (props.webType) {
            // 行业相关筛选
            industryFilters.value = industryFilters.value?.map(industryItem => {
                if (industryItem.webType && industryItem.webType === props.webType) {
                    industryItem.isChecked = true;
                }
                return industryItem;
            });
        }
        if (props.webTarget) {
            // 用户目的相关筛选
            purposeFilters.value = purposeFilters.value?.map(purposeItem => {
                if (purposeItem.webTarget?.includes(props.webTarget as string)) {
                    purposeItem.isChecked = true;
                }
                return purposeItem;
            });
        }
        if (
            industryFilters.value?.some(item => item.isChecked) ||
            purposeFilters.value?.some(item => item.isChecked)
        ) {
            eventBus.$emit('showTip', 'templateFilter');
        }
    }

    eventBus.$on('filterChange', (params: { [key: string]: never[] }) => {
        if (Object.keys(params).length === 0) {
            purposeFilterSelected.value = [];
            industryFilterSelected.value = [];
            typeFilterSelected.value = [];
        } else {
            for (const type in params) {
                switch (type) {
                    case 'purpose':
                        purposeFilterSelected.value = params[type];
                        break;
                    case 'industry':
                        industryFilterSelected.value = params[type];
                        break;
                    case 'type':
                        typeFilterSelected.value = params[type];
                        break;
                }
            }

            if (!params.purpose) {
                purposeFilterSelected.value = [];
            }
            if (!params.industry) {
                industryFilterSelected.value = [];
            }
            if (!params.type) {
                typeFilterSelected.value = [];
            }
        }
    });
});
// onUnmounted
onUnmounted(() => {
    eventBus.$off('filterChange');
});

function initShowTemplate() {
    if (defaultTemplateId.value && allECTemplates.value && officialTemplates.value.length) {
        const allTemplates = cloneDeep(officialTemplates.value).concat(allECTemplates.value);
        const initTemplate = allTemplates.find(tamplate => tamplate.id === defaultTemplateId.value);
        if (initTemplate) {
            defaultTemplateId.value = '';
            handleViewTemplate(initTemplate);
        }
    }
}

function searchFromList(list: string[], str: string) {
    if (!list || list?.length <= 0 || !Array.isArray(list)) {
        return false;
    }
    return list?.some(item => {
        return t(`experience.onboarding.tags.${item}`)?.toLocaleLowerCase()?.indexOf(str) !== -1;
    });
}

function filter(type: 'type' | 'industry' | 'purpose') {
    const arr: any = [];
    addUrlQuery(arr, type);
    switch (type) {
        case 'type':
            typeFilterList.value?.map(item => {
                item.isChecked && arr.push(item.key);
            });
            typeFilterSelected.value = arr;
            break;
        case 'industry':
            industryFilters.value?.map(item => {
                item.isChecked && arr.push(item.key);
            });
            industryFilterSelected.value = arr;
            break;
        case 'purpose':
            purposeFilters.value?.map(item => {
                item.isChecked && arr.push(item.key);
            });
            purposeFilterSelected.value = arr;
            break;
    }
}

function handleViewTemplate(template: ExperienceTemplateV4 | ECTemplate) {
    emit('view', template);
}
function handleCreateTemplate(template: ExperienceTemplateV4 | ECTemplate) {
    emit('create', template);
}

function handleEditTemplate(template: ExperienceTemplateV4) {
    state.currentEditTemplate = template;
    state.editDialogVisible = true;
}

function handleDeleteTemplate(template: ExperienceTemplateV4) {
    state.currentEditTemplate = template;
    state.deleteDialogVisible = true;
}

function handleUpdateOwnTemplate(template: ExperienceTemplateV4) {
    const { id, name, description } = template;
    updateOwnTemplate(
        {
            id,
            name,
            description
        },
        () => {
            PtMessage.success(t('experience.message.update_success'));
            state.editDialogVisible = false;
        }
    );
}

function confirmDeleteTemplate(template: ExperienceTemplateV4) {
    if (template.id) {
        deleteOwnTemplate(template.id, () => {
            PtMessage.success(t('experience.message.delete_success'));
            state.deleteDialogVisible = false;
        });
    }
}

// 通过engage的type
function getECTemplatesByType(key: 'ABTEST' | ExperienceType | undefined) {
    if (!allECTemplates.value) {
        return [];
    }
    if (key === 'ABTEST') {
        return cloneDeep(allECTemplates.value).filter((item: ECTemplate) => item?.tags?.test);
    } else if (key === 'INLINE') {
        return cloneDeep(allECTemplates.value).filter(
            (item: ECTemplate) => item?.tags?.type === 'Web Editing'
        );
    } else if (['POPUP', 'STICKY_BAR'].includes(key as string)) {
        return cloneDeep(allECTemplates.value).filter(
            (item: ECTemplate) => item?.tags?.type === 'Engagement'
        );
    }
    return cloneDeep(allECTemplates.value);
}

function addUrlQuery(item: string[], type: string) {
    if (props.saveFilter && !ecTemplateLoading.value && !officialTemplatesLoading.value) {
        setTimeout(() => {
            const query: any = cloneDeep(route.query);
            query[type] = item.join();

            query[type] = item.length ? item.join() : undefined;
            router.push({
                query,
                params: {
                    savedPosition: JSON.stringify({
                        top: document.documentElement.scrollTop
                    })
                }
            });
        }, 0);
    }
}

function initFilter() {
    const filterType: any = {
        type: typeFilterList.value,
        purpose: purposeFilters.value,
        industry: industryFilters.value
    };
    Object.keys(props.filter).forEach((key: string) => {
        const _filter = filterType[key];
        if (!_filter) {
            return;
        }
        const filterKeys = props.filter[key];
        if (filterKeys) {
            const _filterKeys = filterKeys.split(',');
            _filterKeys.forEach((key: string) => {
                const _index = _filter.findIndex((filter: any) => filter.key === key);
                _index > -1 && (_filter[_index].isChecked = true);
            });
        }
    });
}

function openSuggestDialog() {
    suggest();
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.container {
    position: relative;
    &.noFilter {
        .right {
            padding-left: 0;
            padding-right: 0;
        }
    }
    .noTemplate {
        display: flex;
        flex-direction: column;
        margin-top: 28px;
        align-items: center;
        .img {
            background: url('/images/save_own_template_cn.svg') no-repeat center top;
            background-size: contain;
            width: 100%;
            height: 240px;
            margin-bottom: 56px;
            &.img_jp {
                background: url('/images/save_own_template_jp.svg') no-repeat center top;
            }
            &.img_en {
                background: url('/images/save_own_template_en.svg') no-repeat center top;
            }
        }
        .title {
            text-align: center;
            font-size: 20px;
            line-height: 27px;
            color: $pt-black-900;
            font-weight: 600;
        }
        .desc {
            margin-top: 20px;
            width: 460px;
            text-align: center;
            word-break: break-word;

            :global(.tag) {
                color: #42bea7;
            }
        }
    }
    .search {
        position: absolute;
        right: 36px;
        top: -60px;
        .input {
            width: 340px;
        }
    }
    .sidebar {
        width: 260px;
        padding: 0px 40px 0 40px;
        position: absolute;
        left: 0;
        top: 0;
        .connectUs {
            border-top: 1px solid $pt-black-30;
            font-size: 13px;
            color: $pt-black-900;
            padding-top: 25px;
            .trigger {
                text-decoration: underline;
                cursor: pointer;
            }
        }
    }
    .right {
        width: 100%;
        padding-left: 260px;
        padding-right: 40px;
        box-sizing: border-box;
    }
}
</style>
